import React, { useCallback } from "react";
import { Spinner, Button } from "@heroui/react";
import { Typography, UniversalTable } from "../../../../components";
import { useTranslations } from "../../../../contexts/translation.context";
import { Partner } from "../../../../api/staff";
import { PartnerNameCell, PartnerInfoCell, PartnerIdCell, PartnerStatusCell, PartnerCreatedAtCell, PartnerActionsCell } from "./index";
import { EmptyContentIc } from "../../../../components/icons";

interface PartnersTableProps {
  partners: Partner[];
  isLoading: boolean;
  totalCount: number;
  currentPage: number;
  pageSize: number;
  sortBy?: string;
  onSort?: (sortBy: string) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onRefresh?: () => void;
}

/**
 * PartnersTable Component
 *
 * Main table component for displaying partners.
 * Uses UniversalTable from the component library and custom cell renderers.
 */
const PartnersTable: React.FC<PartnersTableProps> = ({
  partners,
  isLoading,
  totalCount,
  currentPage,
  pageSize,
  sortBy,
  onSort,
  onPageChange,
  onPageSizeChange,
  onRefresh,
}) => {
  const { t } = useTranslations();

  // Define table columns
  const columns = [
    {
      key: "id",
      name: "ID",
      sortable: false
    },
    {
      key: "name",
      name: t('admin.partners.columns.name'),
      sortable: true,
      sortKey: "name"
    },
    {
      key: "email",
      name: t('admin.partners.columns.email'),
      sortable: false
    },
    {
      key: "status",
      name: t('admin.partners.columns.status'),
      sortable: false
    },
    {
      key: "balance",
      name: t('admin.partners.columns.balance'),
      sortable: false
    },
    {
      key: "created_at",
      name: t('admin.partners.columns.createdAt'),
      sortable: true,
      sortKey: "created_at"
    },
    {
      key: "actions",
      name: "",
      sortable: false
    }
  ];

  // Cell renderer
  const renderCell = useCallback((partner: Partner, columnKey: string) => {
    switch (columnKey) {
      case "id":
        return <PartnerIdCell partner={partner} />;
      case "name":
        return <PartnerInfoCell partner={partner} />;
      case "email":
        return (
          <Typography variant="body3" className="text-[#11181C] text-[14px]">
            {partner.email}
          </Typography>
        );
      case "status":
        return <PartnerStatusCell partner={partner} />;
      case "balance":
        return (
          <Typography variant="body3" className="text-[#11181C] text-[14px] font-medium">
            ${typeof partner.balance === 'number' ? partner.balance.toFixed(2) : partner.balance}
          </Typography>
        );
      case "created_at":
        return <PartnerCreatedAtCell partner={partner} />;
      case "actions":
        return <PartnerActionsCell partner={partner} onRefresh={onRefresh} />;
      default:
        return null;
    }
  }, [onRefresh]);

  // Empty content component
  const emptyContent = (
    <div className="flex flex-col items-center justify-center py-[80px] px-[24px]">
      <div className="mb-[16px]">
        <EmptyContentIc/>
      </div>
      <div className="text-center">
        <Typography variant="h6" className="text-[#11181C] mb-[8px]">
          {t('admin.partners.empty.title')}
        </Typography>
        <Typography variant="body2" className="text-[#71717A]">
          {t('admin.partners.empty.description')}
        </Typography>
      </div>
    </div>
  );

  if (isLoading && partners.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  return (
    <UniversalTable
      data={partners}
      columns={columns}
      renderCell={renderCell}
      emptyContent={emptyContent}
      pagination={{
        count: totalCount,
        page_size: pageSize,
        page: currentPage,
      }}
      sortBy={sortBy}
      onSort={onSort}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      currentPage={currentPage}
      currentPageSize={pageSize}
    />
  );
};

export default PartnersTable;
